import { useState, useEffect } from 'react';
import { Shield, CheckCircle, FileText, Lock, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';

const TERMS_ACCEPTED_KEY = 'tempvault_terms_accepted';

export const TermsModal = () => {
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    const hasAccepted = localStorage.getItem(TERMS_ACCEPTED_KEY);
    if (!hasAccepted) {
      setOpen(true);
    }
  }, []);

  const handleAccept = () => {
    if (accepted) {
      localStorage.setItem(TERMS_ACCEPTED_KEY, 'true');
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg glass-premium border-2 border-primary/30 glow-border" hideCloseButton>
        <DialogHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="absolute -inset-3 gradient-bg rounded-full opacity-50 blur-xl animate-pulse-slow" />
              <div className="relative w-16 h-16 rounded-2xl gradient-bg flex items-center justify-center shadow-glow-intense animate-float">
                <Shield className="w-8 h-8 text-primary-foreground" />
              </div>
            </div>
          </div>
          <DialogTitle className="text-2xl font-bold gradient-text">
            Welcome to TempVault
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Please review and accept our terms to continue
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-72 pr-4 my-4">
          <div className="space-y-4 text-sm text-muted-foreground">
            <div className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50">
              <FileText className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">1. Service Description</h4>
                <p>TempVault provides temporary, disposable email addresses for privacy protection. Each inbox is valid for 24 hours and automatically deleted thereafter. This service is provided "as is" without guarantees of availability or delivery.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50">
              <Lock className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">2. Privacy & Data Handling</h4>
                <p>Your inbox is secured with a unique cryptographic token stored only in your browser. We do not collect personal information, track users across sessions, or share data with third parties. All emails and inbox data are permanently deleted after the 24-hour expiration period.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50">
              <Shield className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">3. Acceptable Use Policy</h4>
                <p>You agree NOT to use TempVault for: (a) illegal activities or fraud; (b) harassment, threats, or abuse; (c) distributing malware or phishing; (d) violating others' privacy or rights; (e) circumventing security measures of other services; (f) any activity that violates applicable laws or regulations.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50">
              <Sparkles className="w-5 h-5 text-accent mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">4. User Responsibilities</h4>
                <p>You are solely responsible for all activities conducted through your temporary inbox. Do not use TempVault for receiving sensitive information such as financial data, medical records, or legal documents. You acknowledge that emails may be delayed or undelivered.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50">
              <FileText className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">5. Disclaimers & Limitations</h4>
                <p>TempVault is provided without warranty of any kind. We are not liable for any damages arising from use of this service, including but not limited to: data loss, service interruptions, missed emails, or unauthorized access. Maximum liability is limited to the amount paid for the service (which is zero for free users).</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50">
              <Lock className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">6. Termination</h4>
                <p>We reserve the right to terminate or suspend access to TempVault at any time, without prior notice, for violations of these terms or for any other reason. Upon termination, all associated data will be immediately and permanently deleted.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50">
              <Shield className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">7. Modifications</h4>
                <p>We may update these terms at any time. Continued use of TempVault after changes constitutes acceptance of the new terms. We encourage you to review these terms periodically.</p>
              </div>
            </div>

            <div className="text-xs text-muted-foreground/70 mt-4 p-3 rounded-xl bg-muted/30 border border-border/50">
              <p className="mb-2"><strong>Advertisement Notice:</strong> This site displays advertisements from third-party networks to support free service. By continuing, you acknowledge and consent to seeing ads.</p>
              <p><strong>Last Updated:</strong> January 2025 | <strong>Contact:</strong> support@tempvault.com</p>
            </div>
          </div>
        </ScrollArea>

        <div className="space-y-4">
          <div className="flex items-center space-x-3 p-3 rounded-xl bg-secondary/30 border border-border/50">
            <Checkbox
              id="terms"
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked === true)}
              className="border-primary data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
            <label
              htmlFor="terms"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              I accept the Terms of Service and Privacy Policy
            </label>
          </div>

          <Button
            onClick={handleAccept}
            disabled={!accepted}
            className="w-full h-12 gradient-bg text-primary-foreground shadow-glow hover:shadow-glow-intense transition-all font-bold text-base disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            Continue to TempVault
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
